// src/stores/auth.ts
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth } from '@/firebase/firebase-init';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User
} from 'firebase/auth';
import {
  registerWithEmail,
  loginWithEmail,
  loginWithGoogle,
  loginWithFacebook,
  resetPassword,
  createUserDocument
} from '@/firebase/firebase-auth';
import { isAdmin as checkIsAdmin } from '@/firebase/profile-service';

interface UserState {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<UserState | null>(null);
  const isAdmin = ref<boolean>(false);
  const loading = ref<boolean>(true);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!user.value);
  const currentUser = computed(() => user.value);
  const isUserAdmin = computed(() => isAdmin.value);
  const isLoading = computed(() => loading.value);
  const authError = computed(() => error.value);

  // Actions
  async function initAuth() {
    loading.value = true;
    
    // Check if the current URL is a sign-in link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Get the email from localStorage
      const email = localStorage.getItem('emailForSignIn');
      if (email) {
        try {
          await completeSignInWithEmailLink(email, window.location.href);
          // Remove the email from storage
          localStorage.removeItem('emailForSignIn');
          // Clear the URL without reloading the page
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error("Error completing sign-in with email link:", error);
        }
      }
    }
    
    return new Promise<User | null>((resolve) => {
      // Listen for auth state changes
      const unsubscribe = onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          if (firebaseUser) {
            // User is signed in
            user.value = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL
            };
            isAdmin.value = await checkIsAdmin(firebaseUser.uid);
          } else {
            // User is signed out
            user.value = null;
            isAdmin.value = false;
          }
          
          loading.value = false;
          resolve(firebaseUser);
          
          // No need to unsubscribe since we want to keep listening
          // throughout the application lifecycle
        }
      );
    });
  }
  
  // Registration with email
  async function register(email: string, password: string, displayName: string) {
    try {
      loading.value = true;
      error.value = null;
      
      const newUser = await registerWithEmail(email, password, displayName);
      return newUser;
    } catch (err: any) {
      error.value = err.message || 'Failed to register';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Login with email
  async function login(email: string, password: string) {
    try {
      loading.value = true;
      error.value = null;
      
      const loggedInUser = await loginWithEmail(email, password);
      return loggedInUser;
    } catch (err: any) {
      error.value = err.message || 'Failed to login';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Google sign in
  async function loginGoogle() {
    try {
      loading.value = true;
      error.value = null;
      
      const user = await loginWithGoogle();
      return user;
    } catch (err: any) {
      error.value = err.message || 'Failed to login with Google';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Facebook sign in
  async function loginFacebook() {
    try {
      loading.value = true;
      error.value = null;
      
      const user = await loginWithFacebook();
      return user;
    } catch (err: any) {
      error.value = err.message || 'Failed to login with Facebook';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Password reset
  async function forgotPassword(email: string) {
    try {
      loading.value = true;
      error.value = null;
      
      await resetPassword(email);
    } catch (err: any) {
      error.value = err.message || 'Failed to send password reset email';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Logout
  async function logout() {
    try {
      loading.value = true;
      error.value = null;
      
      await firebaseSignOut(auth);
      user.value = null;
    } catch (err: any) {
      error.value = err.message || 'Failed to logout';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Email link sign in
  async function sendSignInLink(email: string, url: string) {
    try {
      loading.value = true;
      error.value = null;
      
      // Send sign-in email link
      await sendSignInLinkToEmail(auth, email, {
        url: url,
        handleCodeInApp: true
      });
      
      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to send sign-in link';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Complete email link sign in
  async function completeSignInWithEmailLink(email: string, link: string) {
    try {
      loading.value = true;
      error.value = null;
      
      const result = await signInWithEmailLink(auth, email, link);
      return result.user;
    } catch (err: any) {
      error.value = err.message || 'Failed to complete sign-in with email link';
      throw err;
    } finally {
      loading.value = false;
    }
  }
  
  // Check if current URL is a sign-in link
  function isEmailSignInLink(link: string) {
    return isSignInWithEmailLink(auth, link);
  }
  
  function setUser(firebaseUser: User | null) {
    if (firebaseUser) {
      user.value = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL
      };
    } else {
      user.value = null;
    }
  }
  
  function setError(message: string | null) {
    error.value = message;
  }
  
  function clearError() {
    error.value = null;
  }

  return { 
    user, 
    loading, 
    error, 
    isAuthenticated, 
    currentUser, 
    isAdmin: isUserAdmin, 
    isLoading, 
    authError, 
    initAuth,
    register,
    login,
    loginGoogle,
    loginFacebook,
    forgotPassword,
    logout,
    sendSignInLink,
    completeSignInWithEmailLink,
    isEmailSignInLink,
    setUser, 
    setError, 
    clearError 
  };
});
